/**
 * StudentPortal - Student's dedicated portal
 * Dashboard, Timetable, Grades, Assignments, Lessons, Quizzes, Catch-up
 */
import React, { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { cn } from '@/lib/utils';
import { toast } from 'sonner';
import {
  LayoutDashboard, BookOpen, Trophy, ClipboardList, CalendarRange,
  LogOut, GraduationCap, Loader2, FileText, Play, Award, ChevronLeft, ChevronRight,
  Clock, CheckCircle, XCircle, AlertCircle, Bell, Video, Library, BookOpenCheck, DollarSign, Lock, Layers, ArrowLeftRight, ScrollText
} from 'lucide-react';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { ScrollArea } from '@/components/ui/scroll-area';
import { useAuthStore } from '@/lib/store';
import TodaysReadingWidget from '@/components/TodaysReadingWidget';
import { PortalNotificationBell } from '@/components/school/PortalNotificationBell';
import { useTranslation } from 'react-i18next';
import LanguageSwitcher from '@/components/LanguageSwitcher';
import TranslatePageButton from '@/components/TranslatePageButton';
import { ProfileCompletenessBanner } from '@/components/school/ProfileCompletenessBanner';
import FeeGateScreen from '@/components/school/FeeGateScreen';
import PortalEmptyState from '@/components/school/PortalEmptyState';
import WorkflowChecklist from '@/components/workflow/WorkflowChecklist';
import { StudentSemesterView, SemesterPicker, useStudentSemesters } from '@/components/school/StudentSemester';
import StudentTransferRequest from '@/components/school/StudentTransferRequest';
import { GraduationProgressView } from '@/components/school/GraduationProgress';
import { getToken } from '@/lib/authToken';
const API_URL = process.env.REACT_APP_BACKEND_URL;

// Handle View-As preview token from URL
const initPreviewToken = () => {
  const params = new URLSearchParams(window.location.search);
  const pt = params.get('preview_token');
  if (pt) {
    sessionStorage.setItem('school_preview_token', pt);
    window.history.replaceState({}, '', window.location.pathname);
  }
};
initPreviewToken();
// Token resolution: when admin is in "View As" mode, sessionStorage holds a
// fresh student token from /view-as. Otherwise fall back to the regular
// authToken lookup. Otherwise the portalApi fetches authenticate as the ADMIN
// and every /student/* endpoint returns empty.
const getEffectiveToken = () => {
  try {
    const previewToken = sessionStorage.getItem('school_preview_token');
    if (previewToken) return previewToken;
  } catch (_e) { /* sessionStorage unavailable */ }
  return getToken();
};
const portalApi = async (path, opts = {}) => {
  const res = await fetch(`${API_URL}/api/school/portal${path}`, { ...opts, headers: { 'Authorization': `Bearer ${getEffectiveToken()}`, 'Content-Type': 'application/json', ...opts.headers } });
  if (!res.ok) { const err = await res.json().catch(() => ({})); throw new Error(err.detail || 'Request failed'); }
  return res.json();
};

const GRADE_COLORS = {
  'A+': 'text-green-700 bg-green-50', 'A': 'text-green-600 bg-green-50',
  'B+': 'text-blue-700 bg-blue-50', 'B': 'text-blue-600 bg-blue-50',
  'C': 'text-amber-700 bg-amber-50', 'D': 'text-orange-700 bg-orange-50',
  'F': 'text-red-700 bg-red-50',
};

const DAYS = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday'];

// Menu uses i18n keys ('navKey') so labels translate at render time.
const MENU = [
  { id: 'dashboard', navKey: 'nav.dashboard', icon: LayoutDashboard },
  { id: 'semester', navKey: 'nav.my_semester', icon: Layers },
  { id: 'timetable', navKey: 'nav.timetable', icon: CalendarRange },
  { id: 'exams', navKey: 'nav.exams', icon: GraduationCap },
  { id: 'grades', navKey: 'nav.grades', icon: Trophy },
  { id: 'graduation', navKey: 'nav.graduation', icon: ScrollText },
  { id: 'attendance', navKey: 'nav.attendance', icon: CheckCircle },
  { id: 'fees', navKey: 'nav.fees', icon: DollarSign },
  { id: 'assignments', navKey: 'nav.assignments', icon: ClipboardList },
  { id: 'lessons', navKey: 'nav.lessons', icon: BookOpen },
  { id: 'reading-list', navKey: 'nav.reading_list', icon: Library },
  { id: 'bible', navKey: 'nav.bible_plans', icon: BookOpenCheck },
  { id: 'transfer', navKey: 'nav.transfer', icon: ArrowLeftRight },
  { id: 'profile', navKey: 'nav.profile', icon: GraduationCap },
  { id: 'reports', navKey: 'nav.reports', icon: FileText },
  { id: 'quizzes', navKey: 'nav.quizzes', icon: Award },
  { id: 'catchup', navKey: 'nav.catchup', icon: Play },
  { id: 'announcements', navKey: 'nav.announcements', icon: Bell },
];

// ============== STUDENT DASHBOARD ==============
const StudentDashboard = ({ tenantId, onSelectTab }) => {
  const { t } = useTranslation();
  const { data: profile } = useQuery({
    queryKey: ['student-profile'],
    queryFn: () => portalApi('/me'),
  });
  const { data: attendance } = useQuery({
    queryKey: ['student-my-attendance', tenantId],
    queryFn: () => portalApi('/student/attendance').catch(() => ({ summary: {} })),
  });
  const { data: assignments = [] } = useQuery({
    queryKey: ['student-my-assignments-dash', tenantId],
    queryFn: () => portalApi('/student/assignments').catch(() => []),
  });
  const { data: catchup } = useQuery({
    queryKey: ['student-my-catchup-dash', tenantId],
    queryFn: () => portalApi('/student/catchup').catch(() => ({ total: 0 })),
  });
  // Fee statement — one record per student linked to the logged-in account.
  // Endpoint already exists at /api/school/portal/fees/statement; we just
  // surface it on the dashboard instead of forcing the student to dig.
  const { data: feeStatements = [] } = useQuery({
    queryKey: ['student-my-fees-dash', tenantId],
    queryFn: () => portalApi('/fees/statement').catch(() => []),
  });
  const myFees = (feeStatements && feeStatements[0]) || null;

  // Daily verse — rendered in the student's preferred Bible translation if
  // they've picked one in the Bible reader, otherwise KJV.
  const { data: votd } = useQuery({
    queryKey: ['student-votd-dashboard'],
    queryFn: () => fetch(`${API_URL}/api/bible/verse-of-the-day`, {
      headers: { 'Authorization': `Bearer ${getEffectiveToken()}` },
    }).then(r => r.ok ? r.json() : null).catch(() => null),
  });

  const pendingAssignments = assignments.filter(a => !a.submitted).length;
  const fmt = (n) => `R${(Number(n) || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

  return (
    <div className="space-y-6" data-testid="student-dashboard">
      <ProfileCompletenessBanner getToken={getEffectiveToken} onComplete={() => onSelectTab && onSelectTab('profile')} />
      <div>
        <h2 className="text-xl font-bold text-slate-900">Welcome, {profile?.name || 'Student'}</h2>
        {profile?.class_name && <p className="text-sm text-slate-500">Class: {profile.class_name} | Admission: {profile.admission_number}</p>}
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <Card><CardContent className="p-4 text-center">
          <Trophy className="h-5 w-5 mx-auto mb-1 text-amber-500" />
          <p className="text-2xl font-bold">{attendance?.summary?.attendance_rate || 0}%</p>
          <p className="text-xs text-slate-500">{t('attendance_ui.attendance_rate', 'Attendance Rate')}</p>
        </CardContent></Card>
        <Card><CardContent className="p-4 text-center">
          <CheckCircle className="h-5 w-5 mx-auto mb-1 text-green-500" />
          <p className="text-2xl font-bold">{attendance?.summary?.present || 0}</p>
          <p className="text-xs text-slate-500">{t('attendance_ui.present', 'Days Present')}</p>
        </CardContent></Card>
        <Card><CardContent className="p-4 text-center">
          <ClipboardList className="h-5 w-5 mx-auto mb-1 text-indigo-500" />
          <p className="text-2xl font-bold">{pendingAssignments}</p>
          <p className="text-xs text-slate-500">{t('portal.no_assignments', 'Pending Work')}</p>
        </CardContent></Card>
        <Card><CardContent className="p-4 text-center">
          <BookOpen className="h-5 w-5 mx-auto mb-1 text-emerald-500" />
          <p className="text-2xl font-bold">{catchup?.total || 0}</p>
          <p className="text-xs text-slate-500">{t('nav.catchup', 'Catch-up')}</p>
        </CardContent></Card>
      </div>

      {/* Daily Verse — rendered in the student's preferred Bible translation
          (set in the Bible reader's version picker). One-line scripture +
          tap-to-open. Becomes the most-read widget once enabled. */}
      {votd && votd.text && (
        <Card className="bg-gradient-to-br from-indigo-50 to-violet-50 border-indigo-100" data-testid="student-votd-widget">
          <CardContent className="p-4 flex items-start gap-3">
            <BookOpen className="h-5 w-5 text-indigo-500 shrink-0 mt-0.5" />
            <div className="flex-1 min-w-0">
              <p className="text-sm text-slate-700 italic leading-snug">"{votd.text}"</p>
              <div className="flex items-center justify-between mt-1">
                <p className="text-[10px] uppercase tracking-wider text-indigo-500 font-semibold">
                  {votd.book} {votd.chapter}:{votd.verse}
                  {votd.bible_id && <span className="ml-1 text-slate-400 font-normal">· {String(votd.bible_id).toUpperCase()}</span>}
                </p>
                <button
                  onClick={() => onSelectTab && onSelectTab('bible')}
                  className="text-[10px] text-indigo-600 hover:underline"
                  data-testid="votd-open-bible"
                >
                  Read more →
                </button>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Today's Bible reading — shared widget, reads canonical reading plans */}
      <TodaysReadingWidget />

      {/* My Fees — surfaces existing /fees/statement so the student sees their
          balance on the dashboard instead of having to dig into a separate tab. */}
      <Card data-testid="student-fees-widget">
        <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
          <CardTitle className="text-base flex items-center gap-2">
            <DollarSign className="h-5 w-5 text-emerald-600" /> My Fees
          </CardTitle>
          {myFees && (
            <Badge
              className={(myFees.balance > 0
                ? 'bg-red-100 text-red-700'
                : 'bg-emerald-100 text-emerald-700')}
              data-testid="student-fees-status"
            >
              {myFees.balance > 0 ? 'Balance due' : 'Paid in full'}
            </Badge>
          )}
        </CardHeader>
        <CardContent className="space-y-3">
          {!myFees ? (
            <p className="text-sm text-slate-400 text-center py-2" data-testid="student-fees-empty">
              No fee charges on your account yet. Your statement appears here once the bursar bills your class.
            </p>
          ) : (
            <>
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
              <div>
                <p className="text-[10px] text-slate-500 uppercase tracking-wide">Total charges</p>
                <p className="text-xl font-bold text-slate-800" data-testid="student-fees-total">{fmt(myFees.total_charges)}</p>
              </div>
              <div>
                <p className="text-[10px] text-slate-500 uppercase tracking-wide">Paid</p>
                <p className="text-xl font-bold text-emerald-600" data-testid="student-fees-paid">{fmt(myFees.total_payments)}</p>
              </div>
              <div>
                <p className="text-[10px] text-slate-500 uppercase tracking-wide">Balance due</p>
                <p
                  className={`text-xl font-bold ${myFees.balance > 0 ? 'text-red-600' : 'text-slate-400'}`}
                  data-testid="student-fees-balance"
                >
                  {fmt(myFees.balance)}
                </p>
              </div>
            </div>
            {(myFees.charges || []).length > 0 && (
              <div className="border-t pt-2">
                <p className="text-[10px] text-slate-500 uppercase tracking-wide mb-1">Recent charges</p>
                <div className="space-y-1 max-h-32 overflow-y-auto">
                  {(myFees.charges || []).slice(0, 4).map(c => (
                    <div key={c.id} className="flex items-center justify-between text-xs">
                      <span className="text-slate-700">{c.fee_type_name || 'Fee'}</span>
                      <span className="font-medium text-slate-900">{fmt(c.amount)}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}
            <p className="text-[10px] text-slate-400">
              For receipts and full payment history, talk to your school's bursar.
            </p>
            </>
          )}
        </CardContent>
      </Card>

      <div className="grid md:grid-cols-2 gap-4">
        <Card>
          <CardHeader><CardTitle className="text-base">{t('attendance_ui.recent_attendance', 'Recent Attendance')}</CardTitle></CardHeader>
          <CardContent>
            {!attendance?.records?.length ? (
              <p className="text-sm text-slate-400 text-center py-4">{t('attendance_ui.no_records', 'No attendance records yet')}</p>
            ) : (
              <div className="space-y-1">
                {(attendance.records || []).slice(0, 10).map((r, i) => (
                  <div key={`${r.date}-${r.status}-${i}`} className="flex items-center justify-between py-1.5 text-sm border-b last:border-0">
                    <span className="text-slate-600">{r.date}</span>
                    <Badge className={
                      r.status === 'present' ? 'bg-green-100 text-green-700' :
                      r.status === 'absent' ? 'bg-red-100 text-red-700' :
                      r.status === 'late' ? 'bg-amber-100 text-amber-700' : 'bg-blue-100 text-blue-700'
                    }>{r.status}</Badge>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader><CardTitle className="text-base">Upcoming Assignments</CardTitle></CardHeader>
          <CardContent>
            {assignments.filter(a => !a.submitted).length === 0 ? (
              <p className="text-sm text-slate-400 text-center py-4">All caught up!</p>
            ) : (
              <div className="space-y-2">
                {assignments.filter(a => !a.submitted).slice(0, 5).map(a => (
                  <div key={a.id} className="flex items-center justify-between py-2 border-b last:border-0">
                    <div>
                      <p className="font-medium text-sm">{a.title}</p>
                      <p className="text-xs text-slate-500">{a.subject_name}</p>
                    </div>
                    <Badge variant="outline" className="text-xs">{a.due_date || 'No deadline'}</Badge>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

// ============== TIMETABLE ==============
const StudentTimetable = ({ tenantId }) => {
  const { flat, termId, setTermId } = useStudentSemesters(portalApi);
  const { data: slots = [], isLoading } = useQuery({
    queryKey: ['student-my-timetable', tenantId, termId],
    queryFn: () => portalApi(`/student/timetable${termId ? `?term_id=${termId}` : ''}`),
  });

  if (isLoading) return <Loader2 className="h-6 w-6 animate-spin mx-auto" />;

  const grouped = {};
  DAYS.forEach(d => { grouped[d] = []; });
  slots.forEach(s => {
    const day = s.day_of_week || 'Monday';
    if (grouped[day]) grouped[day].push(s);
  });
  Object.values(grouped).forEach(arr => arr.sort((a, b) => (a.start_time || '').localeCompare(b.start_time || '')));

  return (
    <div className="space-y-4" data-testid="student-timetable">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <h2 className="text-lg font-semibold">My Timetable</h2>
        <SemesterPicker flat={flat} termId={termId} setTermId={setTermId}
                        testId="timetable-semester-picker" />
      </div>
      {slots.length === 0 ? (
        <PortalEmptyState title="No timetable yet" kind="timetable" />
      ) : (
        <div className="grid gap-3">
          {DAYS.map(day => (
            <Card key={day}>
              <CardHeader className="py-3 px-4">
                <CardTitle className="text-sm font-semibold">{day}</CardTitle>
              </CardHeader>
              <CardContent className="px-4 pb-3">
                {grouped[day].length === 0 ? (
                  <p className="text-xs text-slate-400">No classes</p>
                ) : (
                  <div className="space-y-1.5">
                    {grouped[day].map((s, i) => (
                      <div key={s.id || `${s.subject_name}-${s.start_time}-${i}`} className="flex items-center justify-between bg-slate-50 rounded-lg px-3 py-2">
                        <div>
                          <p className="font-medium text-sm">{s.subject_name || 'Subject'}</p>
                          <p className="text-xs text-slate-500">{s.teacher_name}</p>
                        </div>
                        <Badge variant="outline" className="text-xs font-mono">{s.start_time} - {s.end_time}</Badge>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
};

// ============== EXAMS (takeable / scheduled / submitted / closed) ==============
// This is the ONLY surface that shows exam access. Grouped by lifecycle so a
// student instantly sees what they can write *right now* vs what's coming /
// already finished. NEVER shows grade/result data — that belongs to My Grades.
const StudentExams = ({ tenantId, onSelectTab }) => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { data: examSchedule, isLoading } = useQuery({
    queryKey: ['student-exams-tab', tenantId],
    queryFn: () => portalApi('/student/exams').catch(() => ({ exams: [] })),
  });

  if (isLoading) return <Loader2 className="h-6 w-6 animate-spin mx-auto" />;
  const exams = examSchedule?.exams || [];

  // Lifecycle buckets — derived from backend state + takeable flag so the
  // student UI never has to guess.
  const now = Date.now();
  const buckets = {
    active: exams.filter(e => e.takeable && !e.submitted),
    upcoming: exams.filter(e => !e.takeable && (e.state === 'upcoming' || (e.opens_at && new Date(e.opens_at).getTime() > now)) && !e.submitted),
    submitted: exams.filter(e => e.submitted),
    closed: exams.filter(e => !e.takeable && !e.submitted && (e.state === 'closed' || e.not_takeable_reason === 'Exam window has closed')),
    other: [],
  };
  // Anything not categorised falls into "other" (e.g. fee-gated, no questions yet)
  for (const e of exams) {
    if (![buckets.active, buckets.upcoming, buckets.submitted, buckets.closed].some(b => b.includes(e))) {
      buckets.other.push(e);
    }
  }

  const renderCard = (e) => {
    const canTake = !!e.takeable;
    const reason = e.not_takeable_reason;
    const submitted = !!e.submitted;
    const btnLabel = submitted
      ? t('exam_state.submitted', 'Submitted')
      : canTake
        ? t('exam_state.take_exam')
        : (reason === 'Exam has not opened yet' ? t('exam_state.not_yet')
          : reason === 'Exam window has closed' ? t('exam_state.closed')
          : reason === 'No questions added to this exam yet' ? t('exam_state.no_questions')
          : reason === 'No subject paper configured yet' ? t('exam_state.setup_pending')
          : t('exam_state.unavailable'));
    return (
      <Card key={e.id} data-testid={`student-exam-tab-${e.id}`}>
        <CardContent className="p-4 flex items-center justify-between gap-3 flex-wrap">
          <div className="min-w-0 flex-1">
            <p className="font-semibold text-sm">{e.name}</p>
            <p className="text-xs text-slate-500">
              {e.exam_type && <span className="capitalize">{e.exam_type} · </span>}
              {e.start_date || ''}{e.end_date && e.end_date !== e.start_date ? ` → ${e.end_date}` : ''}
              {e.subjects?.length > 0 && ` · ${e.subjects.length} ${e.subjects.length > 1 ? t('portal.subjects', 'subjects') : t('portal.subject', 'subject')}`}
              {e.duration_minutes ? ` · ${e.duration_minutes} min` : ''}
            </p>
            <div className="flex items-center gap-2 mt-1.5 flex-wrap">
              <Badge variant="outline" className={
                submitted ? 'text-violet-700 border-violet-300 bg-violet-50' :
                canTake ? 'text-emerald-600 border-emerald-300' :
                e.fee_gated ? 'text-orange-600 border-orange-300 bg-orange-50' :
                reason === 'Exam window has closed' ? 'text-rose-600 border-rose-300' :
                'text-amber-600 border-amber-300'
              } data-testid={`student-exam-tab-state-${e.id}`}>
                {submitted ? t('exam_state.submitted', 'Submitted') :
                  canTake ? t('exam_state.open_now') :
                  e.fee_gated ? <><DollarSign className="h-3 w-3 inline mr-0.5" />{reason}</> :
                  (reason || t('exam_state.unavailable'))}
              </Badge>
              {e.opens_at && e.state === 'upcoming' && (
                <Badge variant="outline" className="text-amber-600 border-amber-300 text-xs">
                  {t('exam_state.opens')} {new Date(e.opens_at).toLocaleString()}
                </Badge>
              )}
              {e.closes_at && e.state === 'active' && !submitted && (
                <Badge variant="outline" className="text-slate-500 border-slate-300 text-xs">
                  {t('exam_state.closes')} {new Date(e.closes_at).toLocaleString()}
                </Badge>
              )}
            </div>
          </div>
          <Button
            size="sm"
            disabled={!canTake || submitted}
            onClick={() => navigate(`/school/${tenantId}/exam/${e.id}/take`)}
            className="h-10 min-w-0 sm:min-w-[110px]"
            data-testid={`student-exam-tab-take-${e.id}`}
            title={submitted ? t('exam_state.submitted', 'Submitted') : (!canTake ? reason : t('exam_state.take_exam'))}
          >
            {btnLabel}
          </Button>
        </CardContent>
      </Card>
    );
  };

  const Section = ({ title, list, testid }) => list.length === 0 ? null : (
    <div data-testid={testid}>
      <h3 className="text-sm font-semibold text-slate-600 uppercase tracking-wide mb-2">{title}</h3>
      <div className="space-y-2">{list.map(renderCard)}</div>
    </div>
  );

  return (
    <div className="space-y-6" data-testid="student-exams">
      <h2 className="text-lg font-semibold">{t('portal.my_exams')}</h2>
      {exams.length === 0 ? (
        <Card className="border-dashed"><CardContent className="py-12 text-center text-slate-400 text-sm">{t('portal.no_upcoming_exams')}</CardContent></Card>
      ) : (
        <>
          <Section title={t('exam_state.active_now', 'Active now')} list={buckets.active} testid="student-exams-active" />
          <Section title={t('exam_state.upcoming', 'Upcoming')} list={buckets.upcoming} testid="student-exams-upcoming" />
          <Section title={t('exam_state.submitted_section', 'Submitted')} list={buckets.submitted} testid="student-exams-submitted" />
          <Section title={t('exam_state.closed_section', 'Closed')} list={buckets.closed} testid="student-exams-closed" />
          <Section title={t('exam_state.other_section', 'Other')} list={buckets.other} testid="student-exams-other" />
          <p className="text-xs text-slate-400 pt-2 border-t" data-testid="student-exams-grades-hint">
            {t('portal.grades_hint', 'Looking for your results? Head to')} <button onClick={() => onSelectTab && onSelectTab('grades')} className="underline" data-testid="student-exams-go-grades">{t('nav.grades', 'My Grades')}</button>.
          </p>
        </>
      )}
    </div>
  );
};

// ============== GRADES — published results only ==============
// NEVER shows assigned/upcoming exams (that belongs to the Exams tab). This
// view is strictly the gradebook: published results, marks, percentage,
// teacher feedback. Drives the user's "My Grades" mental model.
const StudentGrades = ({ tenantId }) => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { flat, termId, setTermId } = useStudentSemesters(portalApi);
  const { data: results, isLoading } = useQuery({
    queryKey: ['student-my-grades', tenantId, termId],
    queryFn: () => portalApi(`/student/grades${termId ? `?term_id=${termId}` : ''}`),
  });

  if (isLoading) return <Loader2 className="h-6 w-6 animate-spin mx-auto" />;

  return (
    <div className="space-y-6" data-testid="student-grades">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <h2 className="text-lg font-semibold" data-testid="student-grades-heading">{t('portal.my_grades')}</h2>
        <SemesterPicker flat={flat} termId={termId} setTermId={setTermId}
                        testId="grades-semester-picker" />
      </div>
      {!results?.results?.length ? (
        <Card className="border-dashed"><CardContent className="py-12 text-center text-slate-400" data-testid="student-grades-empty">{t('portal.no_exam_results')}</CardContent></Card>
      ) : results.results.map((exam, i) => (
        <Card key={exam.exam_name || `exam-${i}`}>
          <CardHeader>
            <div className="flex items-center justify-between">
              <CardTitle className="text-base">{exam.exam_name}</CardTitle>
              <Badge variant="outline" className="text-sm">{exam.percentage}%</Badge>
            </div>
          </CardHeader>
          <CardContent>
            <Table>
              <TableHeader><TableRow>
                <TableHead>Subject</TableHead>
                <TableHead className="text-center">Marks</TableHead>
                <TableHead className="text-center">%</TableHead>
                <TableHead className="text-center">Grade</TableHead>
              </TableRow></TableHeader>
              <TableBody>
                {exam.subjects.map((s, j) => (
                  <TableRow key={j}>
                    <TableCell className="font-medium">{s.subject_name}</TableCell>
                    <TableCell className="text-center">{s.marks}/{s.max_marks}</TableCell>
                    <TableCell className="text-center">{s.percentage}%</TableCell>
                    <TableCell className="text-center">
                      <span className={`px-2 py-0.5 rounded text-xs font-semibold ${GRADE_COLORS[s.grade] || ''}`}>{s.grade}</span>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      ))}
    </div>
  );
};

// ============== GRADUATION CHECKER ==============
const StudentGraduation = ({ tenantId }) => {
  const { data, isLoading, error } = useQuery({
    queryKey: ['student-my-graduation', tenantId],
    queryFn: () => portalApi('/graduation'),
    retry: false,
  });

  return (
    <div className="space-y-4" data-testid="student-graduation">
      <h2 className="text-lg font-semibold" data-testid="student-graduation-heading">My progress to graduation</h2>
      <p className="text-sm text-slate-500">
        Every subject on your course, and which ones you still need to pass.
      </p>
      <GraduationProgressView data={data} isLoading={isLoading} error={error} mode="student" />
    </div>
  );
};


// ============== ATTENDANCE (full history) ==============
const StudentAttendance = ({ tenantId }) => {
  const { data, isLoading } = useQuery({
    queryKey: ['student-my-attendance-full', tenantId],
    queryFn: () => portalApi('/student/attendance'),
  });

  if (isLoading) return <Loader2 className="h-6 w-6 animate-spin mx-auto" />;
  const s = data?.summary || {};

  return (
    <div className="space-y-4" data-testid="student-attendance-page">
      <h2 className="text-lg font-semibold">My Attendance</h2>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <Card><CardContent className="p-4 text-center">
          <p className="text-2xl font-bold text-indigo-600" data-testid="attendance-rate">{s.attendance_rate || 0}%</p>
          <p className="text-xs text-slate-500">Attendance Rate</p>
        </CardContent></Card>
        <Card><CardContent className="p-4 text-center">
          <p className="text-2xl font-bold text-green-600">{s.present || 0}</p>
          <p className="text-xs text-slate-500">Present</p>
        </CardContent></Card>
        <Card><CardContent className="p-4 text-center">
          <p className="text-2xl font-bold text-red-600">{s.absent || 0}</p>
          <p className="text-xs text-slate-500">Absent</p>
        </CardContent></Card>
        <Card><CardContent className="p-4 text-center">
          <p className="text-2xl font-bold text-amber-600">{s.late || 0}</p>
          <p className="text-xs text-slate-500">Late</p>
        </CardContent></Card>
      </div>
      <Card>
        <CardHeader><CardTitle className="text-base">History (last 90 days)</CardTitle></CardHeader>
        <CardContent>
          {!data?.records?.length ? (
            <p className="text-sm text-slate-400 text-center py-6">No attendance records yet. Records appear here once your teacher marks the class register.</p>
          ) : (
            <div className="space-y-1">
              {data.records.map((r, i) => (
                <div key={`${r.date}-${i}`} className="flex items-center justify-between py-1.5 text-sm border-b last:border-0">
                  <span className="text-slate-600">{r.date}</span>
                  <div className="flex items-center gap-2">
                    {r.source && r.source !== 'manual' && <span className="text-[10px] text-slate-400 capitalize">{String(r.source).replace(/_/g, ' ')}</span>}
                    <Badge className={
                      r.status === 'present' ? 'bg-green-100 text-green-700' :
                      r.status === 'absent' ? 'bg-red-100 text-red-700' :
                      r.status === 'late' ? 'bg-amber-100 text-amber-700' : 'bg-blue-100 text-blue-700'
                    }>{r.status}</Badge>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

// ============== FEES (full statement) ==============
const StudentFees = ({ tenantId }) => {
  const { flat, termId, setTermId } = useStudentSemesters(portalApi);
  const [allTerms, setAllTerms] = useState(false);
  const { data: statements = [], isLoading } = useQuery({
    queryKey: ['student-my-fees-full', tenantId, termId, allTerms],
    queryFn: () => portalApi(
      `/fees/statement${!allTerms && termId ? `?term_id=${termId}` : ''}`).catch(() => []),
  });
  const st = statements[0] || null;
  const fmt = (n) => `R${(Number(n) || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

  if (isLoading) return <Loader2 className="h-6 w-6 animate-spin mx-auto" />;

  return (
    <div className="space-y-4" data-testid="student-fees-page">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <h2 className="text-lg font-semibold">My Fees</h2>
        <div className="flex items-center gap-2 flex-wrap">
          {!allTerms && (
            <SemesterPicker flat={flat} termId={termId} setTermId={setTermId}
                            testId="fees-semester-picker" />
          )}
          <label className="flex items-center gap-1.5 text-xs text-slate-600">
            <input type="checkbox" checked={allTerms} data-testid="fees-all-semesters-toggle"
                   onChange={(e) => setAllTerms(e.target.checked)} />
            All semesters
          </label>
        </div>
      </div>
      {!st ? (
        <Card className="border-dashed"><CardContent className="py-12 text-center text-slate-400">
          No fee charges on your account yet. Statements appear here once the bursar bills your class.
        </CardContent></Card>
      ) : (
        <>
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
            <Card><CardContent className="p-4 text-center">
              <p className="text-xl font-bold text-slate-800" data-testid="fees-total-charges">{fmt(st.total_charges)}</p>
              <p className="text-xs text-slate-500">Total Charges</p>
            </CardContent></Card>
            <Card><CardContent className="p-4 text-center">
              <p className="text-xl font-bold text-emerald-600" data-testid="fees-total-paid">{fmt(st.total_payments)}</p>
              <p className="text-xs text-slate-500">Paid</p>
            </CardContent></Card>
            <Card><CardContent className="p-4 text-center">
              <p className={`text-xl font-bold ${st.balance > 0 ? 'text-red-600' : 'text-slate-400'}`} data-testid="fees-balance">{fmt(st.balance)}</p>
              <p className="text-xs text-slate-500">Balance Due</p>
            </CardContent></Card>
          </div>
          <Card>
            <CardHeader><CardTitle className="text-base">Charges</CardTitle></CardHeader>
            <CardContent>
              {!(st.charges || []).length ? (
                <p className="text-sm text-slate-400 text-center py-4">No charges</p>
              ) : (
                <Table>
                  <TableHeader><TableRow>
                    <TableHead>Fee</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead className="text-right">Amount</TableHead>
                  </TableRow></TableHeader>
                  <TableBody>
                    {st.charges.map(c => (
                      <TableRow key={c.id}>
                        <TableCell className="font-medium">{c.fee_type_name || c.description || 'Fee'}</TableCell>
                        <TableCell><Badge variant="outline" className="capitalize">{c.status || 'pending'}</Badge></TableCell>
                        <TableCell className="text-right">{fmt(c.amount)}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>
          <Card>
            <CardHeader><CardTitle className="text-base">Payments</CardTitle></CardHeader>
            <CardContent>
              {!(st.payments || []).length ? (
                <p className="text-sm text-slate-400 text-center py-4">No payments recorded yet</p>
              ) : (
                <Table>
                  <TableHeader><TableRow>
                    <TableHead>Receipt</TableHead>
                    <TableHead>Date</TableHead>
                    <TableHead className="text-right">Amount</TableHead>
                  </TableRow></TableHeader>
                  <TableBody>
                    {st.payments.map(p => (
                      <TableRow key={p.id}>
                        <TableCell className="font-medium">{p.receipt_number || '—'}</TableCell>
                        <TableCell>{(p.payment_date || p.created_at || '').slice(0, 10)}</TableCell>
                        <TableCell className="text-right text-emerald-700">{fmt(p.amount)}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>
          <p className="text-xs text-slate-400">For receipts and payment queries, talk to your school's bursar office.</p>
        </>
      )}
    </div>
  );
};

// ============== ASSIGNMENTS ==============
const StudentAssignments = ({ tenantId }) => {
  const { t } = useTranslation();
  const { data: assignments = [], isLoading } = useQuery({
    queryKey: ['student-my-assignments', tenantId],
    queryFn: () => portalApi('/student/assignments'),
  });

  if (isLoading) return <Loader2 className="h-6 w-6 animate-spin mx-auto" />;

  return (
    <div className="space-y-4" data-testid="student-assignments">
      <h2 className="text-lg font-semibold">{t('portal.my_assignments')}</h2>
      {assignments.length === 0 ? (
        <Card className="border-dashed"><CardContent className="py-8 text-center text-slate-400">{t('portal.no_assignments')}</CardContent></Card>
      ) : (
        <div className="space-y-2">
          {assignments.map(a => {
            const closed = a.state === 'closed';
            const upcoming = a.state === 'upcoming';
            return (
              <Card key={a.id} data-testid={`student-assignment-${a.id}`}>
                <CardContent className="p-4">
                  <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
                    <div className="min-w-0">
                      <h3 className="font-semibold text-sm">{a.title}</h3>
                      <p className="text-xs text-slate-500">
                        {a.subject_name} · Due: {a.due_date || (a.closes_at ? new Date(a.closes_at).toLocaleString() : 'No deadline')} · Max: {a.max_marks || 100}
                      </p>
                      {a.instructions && <p className="text-sm text-slate-600 mt-1 line-clamp-2">{a.instructions}</p>}
                      <div className="flex flex-wrap gap-2 mt-2">
                        {upcoming && (
                          <Badge className="bg-amber-100 text-amber-700">
                            {t('exam_state.opens')} {a.opens_at ? new Date(a.opens_at).toLocaleString() : ''}
                          </Badge>
                        )}
                        {closed && !a.submitted && (
                          <Badge className="bg-rose-100 text-rose-700">
                            {t('exam_state.closed')}{a.closes_at ? ` ${new Date(a.closes_at).toLocaleString()}` : ''}
                          </Badge>
                        )}
                      </div>
                    </div>
                    <div className="text-right shrink-0">
                      {a.submitted ? (
                        <div>
                          <Badge className={a.submission_status === 'graded' ? 'bg-green-100 text-green-700' : 'bg-amber-100 text-amber-700'}>
                            {a.submission_status === 'graded' ? t('exam_state.graded') : t('exam_state.submitted')}
                          </Badge>
                          {a.marks != null && <p className="text-sm font-bold mt-1">{a.marks}/{a.max_marks || 100}</p>}
                          {a.feedback && <p className="text-xs text-slate-500 mt-0.5">{a.feedback}</p>}
                        </div>
                      ) : closed ? (
                        <Badge variant="outline" className="text-slate-500 border-slate-300">{t('exam_state.submission_closed')}</Badge>
                      ) : upcoming ? (
                        <Badge variant="outline" className="text-amber-600 border-amber-300">{t('exam_state.not_opened')}</Badge>
                      ) : (
                        <Badge variant="outline" className="text-orange-600 border-orange-300">{t('exam_state.pending')}</Badge>
                      )}
                    </div>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}
    </div>
  );
};

// ============== LESSONS ==============
const StudentLessons = ({ tenantId }) => {
  const { t } = useTranslation();
  const { data: lessons = [], isLoading } = useQuery({
    queryKey: ['student-lessons', tenantId],
    queryFn: () => portalApi('/student/timetable').then(() => []).catch(() => []),
  });

  // Use the school LMS lessons endpoint
  const { data: lmsLessons = [] } = useQuery({
    queryKey: ['student-lms-lessons', tenantId],
    queryFn: async () => {
      const res = await fetch(`${API_URL}/api/school/tenants/${tenantId}/lessons?published_only=true`, {
        headers: { 'Authorization': `Bearer ${getEffectiveToken()}` }
      });
      return res.ok ? res.json() : [];
    },
  });

  return (
    <div className="space-y-4" data-testid="student-lessons">
      <h2 className="text-lg font-semibold">{t('portal.lessons')}</h2>
      {isLoading ? <Loader2 className="h-5 w-5 animate-spin mx-auto" /> : lmsLessons.length === 0 ? (
        <PortalEmptyState title={t('portal.no_lessons')} kind="lessons" />
      ) : (
        <div className="space-y-2">
          {lmsLessons.map(l => (
            <Card key={l.id}>
              <CardContent className="p-4">
                <div className="flex items-start justify-between">
                  <div>
                    <h3 className="font-medium">{l.title}</h3>
                    <p className="text-xs text-slate-500">{l.subject_name} {l.topic ? `- ${l.topic}` : ''}</p>
                    {l.content && <p className="text-sm text-slate-600 mt-2 line-clamp-3">{l.content}</p>}
                  </div>
                  <div className="flex gap-1 shrink-0 ml-3">
                    {l.video_url && <Badge className="bg-purple-100 text-purple-700 text-xs"><Play className="h-3 w-3 mr-0.5" />Video</Badge>}
                    {l.tags?.length > 0 && l.tags.map(t => <Badge key={t} variant="outline" className="text-xs">{t}</Badge>)}
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
};

// ============== READING LIST (library assignments) ==============
const StudentReadingList = ({ tenantId }) => {
  const qc = useQueryClient();
  const { data: items = [], isLoading } = useQuery({
    queryKey: ['student-reading-list', tenantId],
    queryFn: async () => {
      const res = await fetch(`${API_URL}/api/school/tenants/${tenantId}/library/my-assignments`, {
        headers: { 'Authorization': `Bearer ${getEffectiveToken()}` },
      });
      return res.ok ? res.json() : [];
    },
  });

  const markRead = useMutation({
    mutationFn: async (id) => {
      const res = await fetch(`${API_URL}/api/school/tenants/${tenantId}/library/my-assignments/${id}/mark-read`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${getEffectiveToken()}` },
      });
      if (!res.ok) throw new Error('Failed');
      return res.json();
    },
    onSuccess: () => { toast.success('Marked as read'); qc.invalidateQueries({ queryKey: ['student-reading-list', tenantId] }); },
    onError: () => toast.error('Could not update'),
  });

  const open = (it) => {
    if (it.file_url) window.open(it.file_url, '_blank', 'noreferrer');
  };

  return (
    <div className="space-y-4" data-testid="student-reading-list">
      <h2 className="text-lg font-semibold flex items-center gap-2"><Library className="h-5 w-5" /> My Reading List</h2>
      {isLoading ? (
        <div className="py-10 text-center"><Loader2 className="h-5 w-5 animate-spin mx-auto" /></div>
      ) : items.length === 0 ? (
        <PortalEmptyState title="No books assigned yet" kind="reading_list" />
      ) : (
        <div className="space-y-2">
          {items.map(it => {
            const isDigital = it.resource_type && it.resource_type !== 'physical';
            const done = it.status === 'completed';
            return (
              <Card key={it.id} data-testid={`reading-row-${it.id}`}>
                <CardContent className="p-4">
                  <div className="flex items-start justify-between gap-4">
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2 flex-wrap">
                        <h3 className={`font-medium ${done ? 'line-through text-slate-500' : ''}`}>{it.book_title}</h3>
                        <Badge variant="outline" className="text-[10px]">{it.resource_type || 'physical'}</Badge>
                        {it.mode && it.mode !== 'either' && (
                          <Badge variant="outline" className="text-[10px] capitalize">{it.mode} only</Badge>
                        )}
                        {done && <Badge className="bg-emerald-100 text-emerald-700 text-[10px]">Done</Badge>}
                      </div>
                      <p className="text-xs text-slate-500 mt-0.5">{it.book_author}</p>
                      {it.notes && <p className="text-sm text-slate-600 mt-2">{it.notes}</p>}
                      <div className="flex items-center gap-3 mt-2 text-xs text-slate-500">
                        {it.due_date && (
                          <span className="flex items-center gap-1"><Clock className="h-3 w-3" /> Due {it.due_date}</span>
                        )}
                        {!isDigital && it.shelf_location && (
                          <span>Shelf: {it.shelf_location}</span>
                        )}
                      </div>
                    </div>
                    <div className="flex flex-col gap-2 shrink-0">
                      {isDigital && it.file_url && (
                        <Button size="sm" variant="outline" onClick={() => open(it)} data-testid={`read-now-${it.id}`}>
                          <Play className="h-3.5 w-3.5 mr-1" /> Read now
                        </Button>
                      )}
                      {!done && (
                        <Button size="sm" onClick={() => markRead.mutate(it.id)} disabled={markRead.isPending} data-testid={`mark-read-${it.id}`}>
                          <CheckCircle className="h-3.5 w-3.5 mr-1" /> Mark read
                        </Button>
                      )}
                    </div>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}
    </div>
  );
};

// ============== STUDENT PROFILE EDITOR ==============
// Small wrapper that hits /school/workflow/student-profile/{tid}/{sid}/readiness
// and renders the shared checklist card. Lives next to the editor so the
// "what's missing" panel sits right above the form the student can fix it in.
const StudentProfileReadiness = ({ tenantId, studentId }) => {
  const { data, isLoading } = useQuery({
    queryKey: ['student-profile-readiness', tenantId, studentId],
    queryFn: async () => {
      const r = await fetch(
        `${API_URL}/api/school/workflow/student-profile/${tenantId}/${studentId}/readiness`,
        { headers: { 'Authorization': `Bearer ${getEffectiveToken()}` } },
      );
      if (!r.ok) throw new Error('readiness fetch failed');
      return r.json();
    },
    enabled: !!tenantId && !!studentId,
    refetchOnWindowFocus: true,
  });
  if (!tenantId || !studentId) return null;
  return <WorkflowChecklist data={data} loading={isLoading} data-testid="student-profile-readiness-card" />;
};

const StudentProfile = ({ tenantId }) => {
  const qc = useQueryClient();
  const { data: me } = useQuery({
    queryKey: ['student-profile-me'],
    queryFn: async () => {
      const r = await fetch(`${API_URL}/api/school/portal/me`, {
        headers: { 'Authorization': `Bearer ${getEffectiveToken()}` },
      });
      return r.ok ? r.json() : {};
    },
  });

  // Used to flag fields the completeness banner marked as missing so we can
  // render a red/amber ring around the corresponding input.
  const { data: completeness } = useQuery({
    queryKey: ['student-profile-completeness'],
    queryFn: async () => {
      const r = await fetch(`${API_URL}/api/school/portal/profile/completeness`, {
        headers: { 'Authorization': `Bearer ${getEffectiveToken()}` },
      });
      return r.ok ? r.json() : { summary: [] };
    },
  });
  const missingMap = React.useMemo(() => {
    const m = {};
    (completeness?.summary || []).forEach(s => { if (!s.present) m[s.key] = s.severity; });
    return m;
  }, [completeness]);
  const fieldClass = (key) => {
    const sev = missingMap[key];
    if (sev === 'required') return 'w-full px-3 py-2 border-2 border-red-300 bg-red-50/30 rounded-md text-sm';
    if (sev === 'important') return 'w-full px-3 py-2 border-2 border-amber-300 bg-amber-50/30 rounded-md text-sm';
    return 'w-full px-3 py-2 border rounded-md text-sm';
  };

  const [form, setForm] = useState({});
  React.useEffect(() => {
    if (me) {
      setForm({
        email: me.email || '',
        phone: me.phone || '',
        address: me.address || '',
        emergency_contact_name: me.emergency_contact_name || '',
        emergency_contact_phone: me.emergency_contact_phone || '',
        medical_notes: me.medical_notes || '',
        date_of_birth: me.date_of_birth || '',
        id_number: me.id_number || '',
        nationality: me.nationality || '',
        religion: me.religion || '',
        previous_school: me.previous_school || '',
        guardian_name: me.guardian_name || '',
        guardian_phone: me.guardian_phone || '',
      });
    }
  }, [me]);

  const save = useMutation({
    mutationFn: async (data) => {
      const r = await fetch(`${API_URL}/api/school/portal/me`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${getEffectiveToken()}` },
        body: JSON.stringify(data),
      });
      if (!r.ok) throw new Error((await r.json()).detail || 'Save failed');
      return r.json();
    },
    onSuccess: () => { toast.success('Profile saved'); qc.invalidateQueries({ queryKey: ['student-profile-me'] }); qc.invalidateQueries({ queryKey: ['student-profile', tenantId] }); qc.invalidateQueries({ queryKey: ['student-profile-completeness'] }); qc.invalidateQueries({ queryKey: ['profile-completeness'] }); },
    onError: (e) => toast.error(e.message),
  });

  const uploadPhoto = async (file) => {
    if (!file) return;
    const fd = new FormData();
    fd.append('file', file);
    try {
      const r = await fetch(`${API_URL}/api/school/portal/me/photo`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${getEffectiveToken()}` },
        body: fd,
      });
      if (!r.ok) throw new Error((await r.json()).detail || 'Upload failed');
      toast.success('Photo updated');
      qc.invalidateQueries({ queryKey: ['student-profile-me'] });
      qc.invalidateQueries({ queryKey: ['student-profile', tenantId] });
    } catch (e) {
      toast.error(e.message);
    }
  };

  if (!me) return <div className="py-10 text-center"><Loader2 className="h-5 w-5 animate-spin mx-auto" /></div>;

  return (
    <div className="space-y-5 max-w-2xl" data-testid="student-profile-editor">
      <h2 className="text-lg font-semibold">My Profile</h2>

      {/* Guided checklist — drives the new "Complete My Profile" experience.
          The check_status returns red/amber/green for each step so the student
          knows exactly what's missing. */}
      <StudentProfileReadiness
        tenantId={me.tenant_id || tenantId}
        studentId={me.student_id || me.id}
      />

      {/* Photo */}
      <Card>
        <CardContent className="p-4 flex items-center gap-4">
          <div className="h-20 w-20 rounded-full overflow-hidden bg-slate-100 ring-2 ring-slate-200">
            {me.photo_url ? (
              <img src={me.photo_url.startsWith('http') ? me.photo_url : `${API_URL}${me.photo_url}`} alt="Profile" className="w-full h-full object-cover" />
            ) : (
              <div className="w-full h-full flex items-center justify-center text-slate-400">
                <GraduationCap className="h-8 w-8" />
              </div>
            )}
          </div>
          <div className="flex-1">
            <p className="text-sm font-medium">{me.name}</p>
            <p className="text-xs text-slate-500">{me.admission_number} · {me.class_name || 'No class yet'}</p>
            <label className="inline-flex items-center gap-1 mt-2 text-xs text-indigo-600 cursor-pointer hover:underline" data-testid="student-photo-label">
              <input type="file" accept="image/*" className="hidden" onChange={(e) => uploadPhoto(e.target.files?.[0])} data-testid="student-photo-input" />
              Change photo
            </label>
          </div>
        </CardContent>
      </Card>

      {/* Editable fields */}
      <Card>
        <CardContent className="p-4 space-y-4">
          {completeness && !completeness.is_complete && (
            <div className="flex items-center gap-2 px-3 py-2 rounded-md bg-amber-50 border border-amber-200 text-amber-900 text-xs" data-testid="profile-missing-summary">
              Fields outlined in <span className="text-red-600 font-semibold">red</span> are required;
              fields in <span className="text-amber-600 font-semibold">amber</span> are recommended.
            </div>
          )}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-xs font-medium text-slate-600 block mb-1">Email{missingMap.email ? ' *' : ''}</label>
              <input
                type="email"
                value={form.email || ''}
                onChange={(e) => setForm({ ...form, email: e.target.value })}
                className={fieldClass('email')}
                data-testid="student-profile-email"
              />
            </div>
            <div>
              <label className="text-xs font-medium text-slate-600 block mb-1">Phone{missingMap.phone ? ' *' : ''}</label>
              <input
                type="tel"
                value={form.phone || ''}
                onChange={(e) => setForm({ ...form, phone: e.target.value })}
                className={fieldClass('phone')}
                data-testid="student-profile-phone"
              />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-xs font-medium text-slate-600 block mb-1">Date of birth{missingMap.date_of_birth ? ' *' : ''}</label>
              <input
                type="date"
                value={(form.date_of_birth || '').slice(0, 10)}
                onChange={(e) => setForm({ ...form, date_of_birth: e.target.value })}
                className={fieldClass('date_of_birth')}
                data-testid="student-profile-dob"
              />
            </div>
            <div>
              <label className="text-xs font-medium text-slate-600 block mb-1">ID / Birth cert no.{missingMap.id_number ? ' *' : ''}</label>
              <input
                type="text"
                value={form.id_number || ''}
                onChange={(e) => setForm({ ...form, id_number: e.target.value })}
                className={fieldClass('id_number')}
                data-testid="student-profile-id-number"
              />
            </div>
          </div>
          <div>
            <label className="text-xs font-medium text-slate-600 block mb-1">Home address{missingMap.address ? ' *' : ''}</label>
            <input
              type="text"
              value={form.address || ''}
              onChange={(e) => setForm({ ...form, address: e.target.value })}
              className={fieldClass('address')}
              data-testid="student-profile-address"
            />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-xs font-medium text-slate-600 block mb-1">Guardian / Parent name{missingMap.guardian_name ? ' *' : ''}</label>
              <input
                type="text"
                value={form.guardian_name || ''}
                onChange={(e) => setForm({ ...form, guardian_name: e.target.value })}
                className={fieldClass('guardian_name')}
                data-testid="student-profile-guardian-name"
              />
            </div>
            <div>
              <label className="text-xs font-medium text-slate-600 block mb-1">Guardian phone{missingMap.guardian_phone ? ' *' : ''}</label>
              <input
                type="tel"
                value={form.guardian_phone || ''}
                onChange={(e) => setForm({ ...form, guardian_phone: e.target.value })}
                className={fieldClass('guardian_phone')}
                data-testid="student-profile-guardian-phone"
              />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-xs font-medium text-slate-600 block mb-1">Emergency contact</label>
              <input
                type="text"
                value={form.emergency_contact_name || ''}
                onChange={(e) => setForm({ ...form, emergency_contact_name: e.target.value })}
                placeholder="Parent / guardian"
                className={fieldClass('emergency_contact_name')}
                data-testid="student-profile-emergency-name"
              />
            </div>
            <div>
              <label className="text-xs font-medium text-slate-600 block mb-1">Emergency phone{missingMap.emergency_contact_phone ? ' *' : ''}</label>
              <input
                type="tel"
                value={form.emergency_contact_phone || ''}
                onChange={(e) => setForm({ ...form, emergency_contact_phone: e.target.value })}
                className={fieldClass('emergency_contact_phone')}
                data-testid="student-profile-emergency-phone"
              />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-xs font-medium text-slate-600 block mb-1">Nationality</label>
              <input
                type="text"
                value={form.nationality || ''}
                onChange={(e) => setForm({ ...form, nationality: e.target.value })}
                className={fieldClass('nationality')}
                data-testid="student-profile-nationality"
              />
            </div>
            <div>
              <label className="text-xs font-medium text-slate-600 block mb-1">Religion / faith</label>
              <input
                type="text"
                value={form.religion || ''}
                onChange={(e) => setForm({ ...form, religion: e.target.value })}
                className={fieldClass('religion')}
                data-testid="student-profile-religion"
              />
            </div>
          </div>
          <div>
            <label className="text-xs font-medium text-slate-600 block mb-1">Previous school</label>
            <input
              type="text"
              value={form.previous_school || ''}
              onChange={(e) => setForm({ ...form, previous_school: e.target.value })}
              className={fieldClass('previous_school')}
              data-testid="student-profile-previous-school"
            />
          </div>
          <div>
            <label className="text-xs font-medium text-slate-600 block mb-1">Medical notes (allergies, conditions)</label>
            <textarea
              rows={3}
              value={form.medical_notes || ''}
              onChange={(e) => setForm({ ...form, medical_notes: e.target.value })}
              className={fieldClass('medical_notes')}
              data-testid="student-profile-medical"
            />
          </div>
          <div className="flex justify-end">
            <Button
              onClick={() => save.mutate(form)}
              disabled={save.isPending}
              data-testid="student-profile-save-btn"
            >
              {save.isPending && <Loader2 className="h-4 w-4 animate-spin mr-1" />}
              Save changes
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};


// ============== BIBLE PLANS (assigned by school admin) ==============
const StudentBiblePlans = ({ tenantId }) => {
  const navigate = useNavigate();
  const { data, isLoading } = useQuery({
    queryKey: ['student-bible-plans'],
    queryFn: async () => {
      const res = await fetch(`${API_URL}/api/bible/my-scoped-plans`, {
        headers: { 'Authorization': `Bearer ${getEffectiveToken()}` },
      });
      return res.ok ? res.json() : { sections: [], active_count: 0 };
    },
  });

  const sections = data?.sections || [];

  // Preserve the school context in the URL so:
  //   1. The Bible reader shows the "Reading with <school>" banner.
  //   2. If the student's session ever expires, RequireAuth bounces them
  //      back to /school/<tenantId>/login instead of the generic
  //      Admin/Member/Delegate page.
  const openPlan = (planId) => {
    const qs = tenantId ? `?from=school&scopeId=${tenantId}` : '';
    navigate(`/bible/plans/${planId}/read${qs}`);
  };

  return (
    <div className="space-y-4" data-testid="student-bible-plans">
      <h2 className="text-lg font-semibold flex items-center gap-2"><BookOpenCheck className="h-5 w-5" /> My Bible Plans</h2>
      {isLoading ? (
        <div className="py-10 text-center"><Loader2 className="h-5 w-5 animate-spin mx-auto" /></div>
      ) : sections.length === 0 ? (
        <Card className="border-dashed">
          <CardContent className="py-12 text-center text-slate-400">
            No Bible plans assigned yet. Your school administrator will add plans here.
          </CardContent>
        </Card>
      ) : (
        sections.map((sec) => (
          <div key={sec.scope_id} className="space-y-2">
            <h3 className="text-sm font-medium text-slate-600">{sec.tenant_name}</h3>
            {sec.plans.map((p) => {
              const prog = p.progress;
              const pct = prog ? Math.round(prog.completion_percent || 0) : 0;
              return (
                <Card key={p.id} data-testid={`bible-plan-${p.id}`}>
                  <CardContent className="p-4">
                    <div className="flex items-start justify-between gap-4">
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center gap-2 flex-wrap">
                          <h4 className="font-medium">{p.title}</h4>
                          {p.duration_days && (
                            <Badge variant="outline" className="text-[10px]">{p.duration_days} days</Badge>
                          )}
                          {prog?.status === 'completed' && (
                            <Badge className="bg-emerald-100 text-emerald-700 text-[10px]">Completed</Badge>
                          )}
                        </div>
                        {p.description && <p className="text-sm text-slate-600 mt-1">{p.description}</p>}
                        {prog && (
                          <div className="mt-2">
                            <div className="flex justify-between text-xs text-slate-500 mb-1">
                              <span>Day {prog.current_day} of {p.duration_days}</span>
                              <span>{pct}%</span>
                            </div>
                            <div className="w-full h-1.5 rounded bg-slate-100 overflow-hidden">
                              <div className="h-full bg-indigo-500 transition-all" style={{ width: `${pct}%` }} />
                            </div>
                          </div>
                        )}
                      </div>
                      <Button
                        size="sm"
                        onClick={() => openPlan(p.id)}
                        data-testid={`bible-plan-open-${p.id}`}
                      >
                        {prog ? 'Continue' : 'Start'}
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        ))
      )}
    </div>
  );
};


// ============== REPORT CARDS ==============
// Mirrors the admin SchoolReportCards layout but scoped to the logged-in
// student. Pulls /student/exams-with-results to list available reports, then
// /student/report-card/{exam_id} for the full breakdown. Backend gates by
// publish-on-approval so students never see un-moderated marks.
const StudentReports = ({ tenantId }) => {
  const { data: examData, isLoading: loadingList } = useQuery({
    queryKey: ['student-exams-with-results', tenantId],
    queryFn: () => portalApi('/student/exams-with-results').catch(() => ({ exams: [] })),
  });
  const exams = examData?.exams || [];
  // Default to first exam without using set-state-in-effect — derive at render
  // time using a ref+state pair.
  const [selectedExam, setSelectedExam] = React.useState('');
  const effectiveExam = selectedExam || (exams[0]?.id ?? '');

  const { data: report, isLoading: loadingReport } = useQuery({
    queryKey: ['student-report-card', tenantId, effectiveExam],
    queryFn: () => portalApi(`/student/report-card/${effectiveExam}`),
    enabled: !!effectiveExam,
  });

  if (loadingList) return <Loader2 className="h-6 w-6 animate-spin mx-auto" />;
  if (exams.length === 0) {
    return (
      <Card className="border-dashed" data-testid="student-reports-empty">
        <CardContent className="py-12 text-center text-slate-400">
          <FileText className="h-10 w-10 mx-auto mb-3 opacity-50" />
          No report cards available yet. Once your teacher publishes results, they will appear here.
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-4" data-testid="student-reports-panel">
      <h2 className="text-lg font-semibold flex items-center gap-2">
        <FileText className="h-5 w-5" /> My Report Cards
      </h2>
      <div className="flex flex-wrap gap-2">
        {exams.map((e) => (
          <Button
            key={e.id}
            size="sm"
            variant={effectiveExam === e.id ? 'default' : 'outline'}
            onClick={() => setSelectedExam(e.id)}
            data-testid={`report-pick-${e.id}`}
          >
            {e.name}
            {!e.results_published && <Badge variant="outline" className="ml-2 text-[10px]">Awaiting publish</Badge>}
          </Button>
        ))}
      </div>
      {loadingReport ? (
        <Loader2 className="h-6 w-6 animate-spin mx-auto mt-6" />
      ) : report?.status === 'awaiting_publication' ? (
        <Card className="border-amber-200 bg-amber-50" data-testid="report-awaiting">
          <CardContent className="py-8 text-center text-amber-700">
            <AlertCircle className="h-8 w-8 mx-auto mb-2" />
            {report.message}
          </CardContent>
        </Card>
      ) : report ? (
        <StudentReportCard report={report} />
      ) : null}
    </div>
  );
};

const StudentReportCard = ({ report }) => {
  const r = report || {};
  // Backend may return a gated payload instead of the full report when a
  // student has outstanding fees or the release date hasn't arrived. We
  // render a clear, friendly explanation rather than a broken-looking
  // empty card.
  if (r.gated) {
    return (
      <Card data-testid="student-report-card-gated" className="border-amber-300 bg-amber-50">
        <CardContent className="p-6 text-center space-y-3">
          <Lock className="h-8 w-8 mx-auto text-amber-600" />
          <h3 className="text-lg font-semibold">{r.exam_name || 'Report card'}</h3>
          <p className="text-sm text-slate-700">{r.message}</p>
          {r.reason === 'fees_outstanding' && r.outstanding_amount != null && (
            <div className="inline-flex items-center gap-2 bg-white border border-amber-300 rounded px-4 py-2">
              <DollarSign className="h-4 w-4 text-amber-600" />
              <span className="font-bold">{r.currency || '$'}{Number(r.outstanding_amount).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
              <span className="text-xs text-slate-500">outstanding</span>
            </div>
          )}
          {r.reason === 'not_yet_released' && r.release_date && (
            <p className="text-xs text-slate-500">Releases {new Date(r.release_date).toLocaleString()}</p>
          )}
        </CardContent>
      </Card>
    );
  }
  const summary = r.summary || {};
  const pf = r.pass_fail || {};
  return (
    <Card data-testid="student-report-card">
      <CardHeader>
        <CardTitle className="text-base flex items-center justify-between">
          <span>{r.exam?.name}</span>
          {pf.label && (
            <Badge className={
              pf.classification === 'distinction' ? 'bg-emerald-100 text-emerald-800' :
              pf.classification === 'merit' ? 'bg-blue-100 text-blue-800' :
              pf.classification === 'credit' ? 'bg-indigo-100 text-indigo-800' :
              pf.is_pass ? 'bg-amber-100 text-amber-800' : 'bg-rose-100 text-rose-800'
            }>{pf.label}</Badge>
          )}
        </CardTitle>
        <div className="text-xs text-slate-500">
          {r.class?.name && <>Class: {r.class.name} · </>}
          {r.term?.name && <>Term: {r.term.name}</>}
        </div>
      </CardHeader>
      <CardContent>
        {(r.subjects || []).length > 0 ? (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Subject</TableHead>
                <TableHead className="text-center">Marks</TableHead>
                <TableHead className="text-center">%</TableHead>
                <TableHead className="text-center">Grade</TableHead>
                <TableHead>Remarks</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {r.subjects.map((s, i) => (
                <TableRow key={i}>
                  <TableCell className="font-medium">{s.subject_name}</TableCell>
                  <TableCell className="text-center">{s.marks}/{s.max_marks}</TableCell>
                  <TableCell className="text-center">{s.percentage}%</TableCell>
                  <TableCell className="text-center font-semibold">{s.grade}</TableCell>
                  <TableCell className="text-xs text-slate-500">{s.remark}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        ) : (
          <p className="text-sm text-slate-400 py-4 text-center">No marks recorded yet.</p>
        )}

        <div className="flex gap-6 bg-slate-50 p-4 rounded-lg mt-4 flex-wrap">
          <div className="text-center">
            <p className="text-xl font-bold">{summary.total_marks}/{summary.total_max}</p>
            <p className="text-xs text-slate-500">Total</p>
          </div>
          <div className="text-center">
            <p className="text-xl font-bold text-indigo-600">{summary.percentage}%</p>
            <p className="text-xs text-slate-500">Percentage</p>
          </div>
          <div className="text-center">
            <p className="text-xl font-bold">{summary.grade}</p>
            <p className="text-xs text-slate-500">Grade</p>
          </div>
          {summary.gpa != null && (
            <div className="text-center">
              <p className="text-xl font-bold text-amber-600">{summary.gpa}</p>
              <p className="text-xs text-slate-500">GPA</p>
            </div>
          )}
          {r.position && (
            <div className="text-center">
              <p className="text-xl font-bold text-green-600">{r.position}/{r.class_size}</p>
              <p className="text-xs text-slate-500">Position</p>
            </div>
          )}
        </div>

        {r.attendance?.total_days > 0 && (
          <div className="mt-4 text-sm">
            <span className="font-semibold text-slate-600">Attendance:</span>
            <span className="ml-2">{r.attendance.present}/{r.attendance.total_days} days · {r.attendance.rate}%</span>
          </div>
        )}

        {(r.comments?.class_teacher || r.comments?.head_teacher) && (
          <div className="mt-3 space-y-1 border-t pt-3 text-sm">
            {r.comments.class_teacher && (
              <p><span className="font-semibold text-slate-600">Class teacher:</span> {r.comments.class_teacher}</p>
            )}
            {r.comments.head_teacher && (
              <p><span className="font-semibold text-slate-600">Head teacher:</span> {r.comments.head_teacher}</p>
            )}
            <p className="text-xs text-slate-500">
              Conduct: <span className="capitalize">{r.comments.conduct}</span>
              {r.comments.effort && <> · Effort: <span className="capitalize">{r.comments.effort}</span></>}
            </p>
          </div>
        )}
      </CardContent>
    </Card>
  );
};


// ============== QUIZZES ==============
const StudentQuizzes = ({ tenantId }) => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { data: quizzes = [], isLoading } = useQuery({
    queryKey: ['student-my-quizzes', tenantId],
    queryFn: () => portalApi('/student/quizzes'),
  });

  if (isLoading) return <Loader2 className="h-6 w-6 animate-spin mx-auto" />;

  // State → button copy + colour. Centralised so we never get out of sync.
  const stateBadge = (q) => {
    if (q.state === 'upcoming') return { label: q.opens_at ? `${t('exam_state.opens')} ${new Date(q.opens_at).toLocaleString()}` : t('exam_state.upcoming'), tone: 'amber' };
    if (q.state === 'closed')   return { label: q.closes_at ? `${t('exam_state.closed')} ${new Date(q.closes_at).toLocaleDateString()}` : t('exam_state.closed'), tone: 'rose' };
    if (!q.can_attempt && q.attempt_count > 0) return { label: t('exam_state.max_attempts_reached'), tone: 'slate' };
    return { label: t('exam_state.available_now'), tone: 'emerald' };
  };
  const toneClass = (tone) => ({
    amber: 'bg-amber-100 text-amber-700',
    rose: 'bg-rose-100 text-rose-700',
    slate: 'bg-slate-100 text-slate-600',
    emerald: 'bg-emerald-100 text-emerald-700',
  }[tone] || 'bg-slate-100');

  return (
    <div className="space-y-4" data-testid="student-quizzes">
      <h2 className="text-lg font-semibold">{t('portal.my_quizzes')}</h2>
      {quizzes.length === 0 ? (
        <Card className="border-dashed"><CardContent className="py-8 text-center text-slate-400">{t('portal.no_quizzes')}</CardContent></Card>
      ) : (
        <div className="space-y-2">
          {quizzes.map(q => {
            const badge = stateBadge(q);
            const canStart = q.state === 'active' && q.can_attempt;
            return (
              <Card key={q.id} data-testid={`student-quiz-${q.id}`}>
                <CardContent className="p-4">
                  <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                    <div className="min-w-0">
                      <h3 className="font-semibold text-sm">{q.title}</h3>
                      <p className="text-xs text-slate-500">
                        {q.subject_name} · {q.questions?.length || 0} questions
                        {q.time_limit_minutes ? ` · ${q.time_limit_minutes} min` : ''}
                        {q.max_attempts ? ` · Max ${q.max_attempts} attempt${q.max_attempts > 1 ? 's' : ''}` : ''}
                      </p>
                      <div className="flex items-center gap-2 mt-2 flex-wrap">
                        <Badge className={toneClass(badge.tone)}>{badge.label}</Badge>
                        {q.attempt_count > 0 && (
                          <Badge variant="outline" className="text-indigo-600 border-indigo-300">
                            {q.attempt_count} attempt{q.attempt_count > 1 ? 's' : ''}
                            {q.best_score != null ? ` · best ${q.best_score}%` : ''}
                          </Badge>
                        )}
                      </div>
                    </div>
                    <Button
                      size="sm"
                      disabled={!canStart}
                      onClick={() => navigate(`/school/${tenantId}/quiz/${q.id}/take`)}
                      className="h-10 min-w-0 sm:min-w-[110px]"
                      data-testid={`student-quiz-start-${q.id}`}
                    >
                      {q.state === 'upcoming' ? 'Not yet open'
                        : q.state === 'closed' ? 'Closed'
                        : q.attempt_count > 0 ? (q.can_attempt ? 'Retake' : 'Done')
                        : 'Start'}
                    </Button>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}
    </div>
  );
};

// ============== CATCH-UP ==============
const StudentCatchup = ({ tenantId }) => {
  const { data, isLoading } = useQuery({
    queryKey: ['student-my-catchup', tenantId],
    queryFn: () => portalApi('/student/catchup'),
  });

  if (isLoading) return <Loader2 className="h-6 w-6 animate-spin mx-auto" />;

  const items = data?.items || [];

  return (
    <div className="space-y-4" data-testid="student-catchup">
      <h2 className="text-lg font-semibold">Catch-up Library</h2>
      {items.length === 0 ? (
        <Card className="border-dashed"><CardContent className="py-8 text-center text-slate-400">No catch-up content available</CardContent></Card>
      ) : (
        <div className="space-y-2">
          {items.map((item, i) => (
            <Card key={item.id || item.title || `item-${i}`}>
              <CardContent className="p-4">
                <div className="flex items-start justify-between">
                  <div>
                    <h3 className="font-medium text-sm">{item.title}</h3>
                    <p className="text-xs text-slate-500">{item.subject_name || ''} {item.topic ? `- ${item.topic}` : ''}</p>
                    {item.content && <p className="text-sm text-slate-600 mt-1 line-clamp-2">{item.content}</p>}
                  </div>
                  <div className="shrink-0 ml-3 flex gap-1">
                    <Badge className={item.type === 'recording' ? 'bg-rose-100 text-rose-700' : 'bg-indigo-100 text-indigo-700'}>
                      {item.type === 'recording' ? <><Play className="h-3 w-3 mr-0.5" />Recording</> : <><FileText className="h-3 w-3 mr-0.5" />Lesson</>}
                    </Badge>
                    {item.video_url && <Badge className="bg-purple-100 text-purple-700 text-xs"><Play className="h-3 w-3 mr-0.5" />Video</Badge>}
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
};


// ============== ANNOUNCEMENTS ==============
const StudentAnnouncements = ({ tenantId }) => {
  const queryClient = useQueryClient();
  const { data, isLoading } = useQuery({
    queryKey: ['student-portal-announcements', tenantId],
    queryFn: () => portalApi('/announcements'),
  });

  const markRead = useMutation({
    mutationFn: (id) => portalApi(`/announcements/${id}/read`, { method: 'POST' }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['student-portal-announcements'] }),
  });

  const announcements = data?.announcements || [];
  const unreadCount = data?.unread_count || 0;

  return (
    <div className="space-y-4" data-testid="student-announcements">
      <div className="flex items-center gap-2">
        <h2 className="text-lg font-semibold">Announcements</h2>
        {unreadCount > 0 && <Badge className="bg-red-500 text-white">{unreadCount} new</Badge>}
      </div>
      {isLoading ? <Loader2 className="h-6 w-6 animate-spin mx-auto" /> : announcements.length === 0 ? (
        <PortalEmptyState title="No announcements" kind="announcements" />
      ) : (
        <div className="space-y-2">
          {announcements.map(a => (
            <Card key={a.id} className={!a.is_read ? 'border-emerald-200 bg-emerald-50/30 cursor-pointer' : ''}
              onClick={() => !a.is_read && markRead.mutate(a.id)}>
              <CardContent className="p-4">
                <div className="flex items-center gap-2 mb-1">
                  {!a.is_read && <div className="w-2 h-2 rounded-full bg-emerald-500" />}
                  <h3 className="font-semibold text-sm">{a.title}</h3>
                  {a.priority === 'urgent' && <Badge className="bg-red-100 text-red-700 text-xs">Urgent</Badge>}
                  {a.priority === 'high' && <Badge className="bg-orange-100 text-orange-700 text-xs">High</Badge>}
                </div>
                <p className="text-sm text-slate-600 whitespace-pre-wrap">{a.body}</p>
                <p className="text-xs text-slate-400 mt-2">By {a.author_name} | {new Date(a.created_at).toLocaleDateString()}</p>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
};


// ============== MAIN STUDENT PORTAL ==============
const StudentPortal = () => {
  const { tenantId } = useParams();
  const navigate = useNavigate();
  const { logout } = useAuthStore();
  const { t } = useTranslation();
  const [activeTab, setActiveTab] = useState('dashboard');
  const [collapsed, setCollapsed] = useState(false);

  const { data: tenant } = useQuery({
    queryKey: ['school-tenant-public', tenantId],
    queryFn: async () => { const res = await fetch(`${API_URL}/api/school/tenants/${tenantId}/public`); return res.ok ? res.json() : null; },
  });

  const brandColor = tenant?.primary_color || '#10b981';

  // Fee lockout status
  const { data: lockoutStatus } = useQuery({
    queryKey: ['fee-lockout-status'],
    queryFn: async () => {
      const res = await fetch(`${API_URL}/api/school/portal/fee-lockout-status`, {
        headers: { 'Authorization': `Bearer ${getEffectiveToken()}` },
      });
      return res.ok ? res.json() : { locked: false, locked_features: [], message: '' };
    },
  });

  const isFeatureLocked = (featureId) => {
    if (!lockoutStatus?.locked) return false;
    return (lockoutStatus.locked_features || []).includes(featureId);
  };

  const LockedOverlay = () => (
    <div className="flex flex-col items-center justify-center py-16 text-center gap-4" data-testid="feature-locked-notice">
      <div className="w-16 h-16 rounded-full bg-red-100 flex items-center justify-center">
        <AlertCircle className="h-8 w-8 text-red-500" />
      </div>
      <h2 className="text-lg font-bold text-slate-800">Access Restricted</h2>
      <p className="text-slate-500 max-w-md text-sm">{lockoutStatus?.message || 'Your account has restricted access due to outstanding fees.'}</p>
    </div>
  );

  const renderContent = () => {
    const featureMap = { timetable: 'timetable', grades: 'grades', assignments: 'assignments', lessons: 'lms', quizzes: 'lms', catchup: 'library' };
    const mapped = featureMap[activeTab];
    if (mapped && isFeatureLocked(mapped)) return <LockedOverlay />;

    switch (activeTab) {
      case 'dashboard': return <StudentDashboard tenantId={tenantId} onSelectTab={setActiveTab} />;
      case 'semester': return <StudentSemesterView portalApi={portalApi} />;
      case 'transfer': return <StudentTransferRequest portalApi={portalApi} />;
      case 'timetable': return <StudentTimetable tenantId={tenantId} />;
      case 'exams': return <StudentExams tenantId={tenantId} onSelectTab={setActiveTab} />;
      case 'grades': return <StudentGrades tenantId={tenantId} />;
      case 'graduation': return <StudentGraduation tenantId={tenantId} />;
      case 'attendance': return <StudentAttendance tenantId={tenantId} />;
      case 'fees': return <StudentFees tenantId={tenantId} />;
      case 'assignments': return <StudentAssignments tenantId={tenantId} />;
      case 'lessons': return <StudentLessons tenantId={tenantId} />;
      case 'reading-list': return <StudentReadingList tenantId={tenantId} />;
      case 'bible': return <StudentBiblePlans tenantId={tenantId} />;
      case 'profile': return <StudentProfile tenantId={tenantId} />;
      case 'reports': return <StudentReports tenantId={tenantId} />;
      case 'quizzes': return <StudentQuizzes tenantId={tenantId} />;
      case 'catchup': return <StudentCatchup tenantId={tenantId} />;
      case 'announcements': return <StudentAnnouncements tenantId={tenantId} />;
      default: return <StudentDashboard tenantId={tenantId} onSelectTab={setActiveTab} />;
    }
  };

  // Global fee gate — when admin has enabled lockout with the 'all' feature,
  // we replace the entire portal with a single Access Restricted screen.
  if (lockoutStatus?.is_global_lock) {
    return (
      <FeeGateScreen
        status={lockoutStatus}
        onLogout={() => { logout(); navigate(`/school/${tenantId}/login`); }}
      />
    );
  }

  return (
    <TooltipProvider delayDuration={0}>
      <div className="flex h-screen bg-slate-50" data-testid="student-portal">
        <div className={cn("flex flex-col h-full bg-slate-900 text-white transition-all duration-300 shrink-0", collapsed ? "w-[68px]" : "w-full max-w-[220px]")}>
          <div className="p-4 border-b border-slate-800">
            <div className="flex items-center justify-between">
              {!collapsed && (
                <div className="flex items-center gap-2">
                  <div className="w-8 h-8 rounded-lg flex items-center justify-center" style={{ backgroundColor: brandColor }}>
                    <GraduationCap className="h-4 w-4 text-white" />
                  </div>
                  <div>
                    <p className="font-bold text-sm">{tenant?.name}</p>
                    <p className="text-[10px] text-slate-400">Student Portal</p>
                  </div>
                </div>
              )}
              <Button variant="ghost" size="icon" onClick={() => setCollapsed(!collapsed)} className="text-slate-400 hover:text-white hover:bg-slate-800">
                {collapsed ? <ChevronRight className="h-4 w-4" /> : <ChevronLeft className="h-4 w-4" />}
              </Button>
            </div>
          </div>

          <ScrollArea className="flex-1 py-2">
            <div className="px-2 space-y-0.5">
              {MENU.map(item => (
                <Tooltip key={item.id}>
                  <TooltipTrigger asChild>
                    <button onClick={() => setActiveTab(item.id)}
                      className={cn("w-full flex items-center gap-3 rounded-lg text-sm transition-colors",
                        collapsed ? "justify-center p-2.5" : "px-3 py-2",
                        activeTab === item.id ? "bg-emerald-600 text-white" : "text-slate-400 hover:text-white hover:bg-slate-800")}
                      data-testid={`student-nav-${item.id}`}>
                      <item.icon className="h-4 w-4 shrink-0" />
                      {!collapsed && <span>{t(item.navKey)}</span>}
                    </button>
                  </TooltipTrigger>
                  {collapsed && <TooltipContent side="right">{t(item.navKey)}</TooltipContent>}
                </Tooltip>
              ))}
              <Tooltip>
                <TooltipTrigger asChild>
                  <button onClick={() => navigate(`/school/${tenantId}/live-classes`)}
                    className={cn("w-full flex items-center gap-3 rounded-lg text-sm transition-colors",
                      collapsed ? "justify-center p-2.5" : "px-3 py-2",
                      "text-slate-400 hover:text-white hover:bg-slate-800")}
                    data-testid="student-nav-live-classes">
                    <Video className="h-4 w-4 shrink-0" />
                    {!collapsed && <span>{t('nav.live_classes')}</span>}
                  </button>
                </TooltipTrigger>
                {collapsed && <TooltipContent side="right">{t('nav.live_classes')}</TooltipContent>}
              </Tooltip>
            </div>
          </ScrollArea>

          <div className="p-3 border-t border-slate-800 space-y-2">
            {!collapsed && <LanguageSwitcher compact />}
            <Button variant="ghost" size={collapsed ? "icon" : "sm"} onClick={() => { logout(); navigate(`/school/${tenantId}/login`); }}
              className={cn("text-slate-400 hover:text-white hover:bg-slate-800", !collapsed && "w-full justify-start gap-2")}
              data-testid="student-logout-btn">
              <LogOut className="h-4 w-4" />{!collapsed && <span>{t('common.logout')}</span>}
            </Button>
          </div>
        </div>

        <div className="flex-1 overflow-auto">
          {sessionStorage.getItem('school_preview_token') && (
            <div className="bg-amber-500 text-white px-4 py-2 text-sm text-center font-medium flex items-center justify-center gap-2" data-testid="preview-banner">
              <AlertCircle className="h-4 w-4" /> Admin Preview Mode — Viewing as Student
              <button onClick={() => { sessionStorage.removeItem('school_preview_token'); window.close(); }} className="ml-4 underline hover:no-underline">Exit Preview</button>
            </div>
          )}
          <div className="sticky top-0 z-10 bg-white/80 backdrop-blur-sm border-b px-6 py-3 flex justify-end gap-2 items-center">
            <TranslatePageButton />
            <PortalNotificationBell onClick={() => setActiveTab('announcements')} className="text-slate-600" />
          </div>
          <div className="p-6 max-w-5xl mx-auto">{renderContent()}</div>
        </div>
      </div>
    </TooltipProvider>
  );
};

export default StudentPortal;
